/**
 * 🔒 The Deal value the banner reads and the stored value follows, from the best
 * information the Deal holds (decision 0726, amending 0665 and 0334). Product owner, 2026-09-24: *"Deal
 * value is set via best info available."* The order is fixed: a sent quote's
 * total, else the bill of materials priced at the item minimum margins (the
 * floor) where it stands above the value, else the Budget Target, else the value
 * as entered. The floor raises the value and never lowers it (decision 2090).
 *
 * 🔨 The source is named and the figures behind it are not: the banner never
 * draws the floor or the cost as figures of their own (decision 0722).
 *
 * 🔒 A service request reads two sources of its own (decision 1912): the billing
 * minimum until an invoice from its job issues, then the invoices issued.
 */
export type DealValueSource = 'quote' | 'bom' | 'budget' | 'entered' | 'requestMinimum' | 'invoiced';

export interface DealValueInputs {
  /** The newest sent quote's total, net of tax, in cents; null where no quote has reached the customer. */
  sentQuoteTotalCents: number | null;
  /** The floor over the Deal's bills of materials in cents; zero or null where no line is priced. */
  floorCents: number | null;
  /** The Budget Target in cents; null where no pair is saved. */
  budgetTargetCents: number | null;
  /** The value as it stands on the Deal, in cents: the last source, typed or last written through. */
  enteredCents: number;
}

export interface DealValueReading {
  cents: number;
  source: DealValueSource;
}

/** The value from the best information available, and which information it came from (decision 0726). */
export function dealValueFromBestInformation(inputs: DealValueInputs): DealValueReading {
  if (inputs.sentQuoteTotalCents !== null) return { cents: inputs.sentQuoteTotalCents, source: 'quote' };
  /*
   * 🔒 The floor is a minimum, never the value (decision 2090, amending 0726). The owner's
   * answer of 2026-10-05: *"It should only raise not lower price and yes history should
   * reflect that."* A value standing above the floor stays as it stands; one at the
   * floor reads as the floor, which is where a raise put it.
   */
  if (inputs.floorCents !== null && inputs.floorCents > 0) return inputs.enteredCents > inputs.floorCents ? { cents: inputs.enteredCents, source: 'entered' } : { cents: inputs.floorCents, source: 'bom' };
  if (inputs.budgetTargetCents !== null) return { cents: inputs.budgetTargetCents, source: 'budget' };
  return { cents: inputs.enteredCents, source: 'entered' };
}

/**
 * 🔒 Whether the budget is too low for the specified job: the value stands above
 * the Budget Maximum (decision 0726). No Maximum saved warns of nothing.
 */
export function budgetTooLow(valueCents: number, budgetMaximumCents: number | null): boolean {
  return budgetMaximumCents !== null && valueCents > budgetMaximumCents;
}

/** 🔒 The Next Action row that leads the list while the budget is too low (decision 0726). */
export const BUDGET_TOO_LOW_ACTION_ID = 'budgetTooLow';

/** The quote statuses that mean a quote reached the customer and still stands (decision 0726). */
export const SENT_QUOTE_STATUSES = ['Sent', 'ClientViewed', 'Accepted'] as const;
